import { useEffect, useState, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import { useEmpresa } from '../contexts/EmpresaContext'
import { useIbsCbsDescricoes } from '../hooks/useIbsCbsDescricoes'
import { AbasIbsCbs, TabelaIbsCbsMono, IbsCbsMonoLinha } from '../components/IbsCbsMono'

// Consulta de Notas Fiscais de Entrada — equivalente web da WCCO011B/WCCO011
// legada. Só consulta (não edita nada); lista o que já foi digitado/lançado
// em frota.ntvcad. Filtros simplificados a pedido do usuário: sem Conta
// Aplicação, Observação, Não Conformidades e Tipo Fechamento.

interface LinhaNota {
  empcod: number
  serie: string
  numero: number
  ftvTipdco: string
  modelo: string
  tnoCod: number | null
  naturezaDes: string
  dataEmissao: string | null
  dataRecebimento: string | null
  cliCod: string
  fornecedor: string
  cprCod: number | null
  comprador: string
  valorMercadoria: number
  valorTotal: number
  observacao: string
  usuarioLancamento: string
  chave: string
  documento: '' | 'DANFE' | 'DACTE' | 'DANFSE'
}

interface ItemDetalhe {
  pnv_lin: number; its_cod: number; pnv_des: string; pnv_de1: string; pnv_un: string
  pnv_qt_ven: string; pnv_vl_mer: string; pnv_precus?: string; pnv_ncm: string
  pnv_servi: string; nbsDes: string // NBS / Serviço Prestado (frota.itspre)
  pnv_orig: string; pnv_dest: string; pnv_ccucod: string; pnv_aplcod: string
  pnv_vl_fre: string; pnv_vl_acr: string; pnv_vl_seg: string; pnv_vl_dsc: string
  cfop: number | null; naturezaItemDes: string; origemDes: string; destinoDes: string; ccuDes: string; aplDes: string
  // Frota/Consumo (Ordem de Serviço) — só vem preenchido quando o destino exige veículo.
  pnv_vei: string; pnv_odo: number | null; pnv_oser: number | null; pnv_oslin: number | null; pnv_tipser: number | null
  veiculoDes: string; tipoServicoDes: string; ordemServicoExecutada: boolean | null
  // Pneu vinculado ao item — recapagem só vem preenchido se o item já passou
  // pelo modal de Recapagem/Conserto (frota.pnucus).
  pnv_pnucod: number | null; pnv_pnurec: string; pneuDes: string
  recapagem: { procedimento: string; valor: number; fornecedoraDes: string; desenhoDes: string; novaVida: string } | null
  // Rateio de verdade (mais de uma Conta Aplicação/Centro de Custo) — frota.ntvccu.
  // Quando o item não tem rateio, vem com 1 linha só (a própria conta do item).
  rateio: { seq: number; centroCustoCod: string; centroCustoDes: string; aplicacaoCod: string; aplicacaoDes: string; percentual: number; valor: number }[]
  pnv_bt_icm: string; pnv_pe_icm: string; pnv_vl_icm: string; pnv_tricod: string
  pnv_bt_sub: string; pnv_pe_sub: string; pnv_vl_sub: string
  pnv_bt_ipi: string; pnv_pe_ipi: string; pnv_vl_ipi: string; pnv_triipi: string
  pnv_bt_pis: string; pnv_pe_pis: string; pnv_vl_pis: string; pnv_tripis: string
  pnv_bt_cof: string; pnv_pe_cof: string; pnv_vl_cof: string; pnv_tricof: string
  pnv_bt_ins: string; pnv_pe_ins: string; pnv_vl_ins: string
  pnv_is_bas: string; pnv_is_are: string
  pnv_cb_bas: string; pnv_cb_bce: string; pnv_cb_are: string; pnv_cb_ral: string; pnv_cb_aef: string; pnv_cb_val: string
  pnv_ib_est: string; pnv_ib_bce: string; pnv_ib_are: string; pnv_ib_ral: string; pnv_ib_aef: string; pnv_ib_val: string
  pnv_mu_bas: string; pnv_mu_bce: string; pnv_mu_are: string; pnv_mu_ral: string; pnv_mu_aef: string; pnv_mu_val: string
  pnv_cstrtc: string; pnv_clartc: string
  ibsCbsMono?: IbsCbsMonoLinha[]; ibsCbsMonoDoXml?: boolean
  pnv_pisxml_bas: string; pnv_pisxml_ali: string; pnv_pisxml_val: string; pnv_pisxml_cst: string
  pnv_cofxml_bas: string; pnv_cofxml_ali: string; pnv_cofxml_val: string; pnv_cofxml_cst: string
}

interface ParcelaDetalhe {
  numero: string; vencimento: string; valor: number; saldo: number; contaCod?: string; contaDes?: string
}

interface CapaDetalhe {
  empcod: number; empNome: string
  serie: string; numero: number
  ftvTipdco: string; ftvNumero: number; modelo: string; chave: string | null
  tnoCod: number | null; tnoCdf: number | null; naturezaDes: string
  dataEmissao: string | null; dataRecebimento: string | null
  cliCod: string; fornecedorNome: string
  cprCod: number | null; comprador: string
  valorMercadoria: number; valorTotal: number
  valorFrete: number; valorSeguro: number; valorDesconto: number; valorAcrescimo: number
  retIrrf: number; retInss: number; retIss: number; retPis: number; retCofins: number; retCsll: number
  observacao: string
}

interface Filtros {
  empcodDe: string
  empcodAte: string
  dataDe: string
  dataAte: string
  tipoData: 'recebimento' | 'emissao'
  ftvTipdco: string
  ftvTipdcoDes: string
  modelo: string
  modeloDes: string
  tnoCod: string
  tnoDes: string
  cprCod: string
  cprDes: string
  cliCod: string
  cliDes: string
  serie: string
  numero: string
  valorDe: string
  valorAte: string
}

function hojeISO() { return new Date().toISOString().slice(0, 10) }

function fmtData(iso: string | null): string {
  if (!iso || iso.slice(0, 4) <= '0001') return '-'
  return iso.slice(0, 10).split('-').reverse().join('/')
}
function fmtVal(v: number | string): string {
  return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
}
function fmtCnpj(doc: string): string {
  const n = (doc || '').replace(/\D/g, '')
  if (n.length === 14) return n.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  if (n.length === 11) return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  return doc || '-'
}

// Cada seção do modal de detalhe tem uma cor de destaque própria — só pra dar
// pistas visuais rápidas (identificação=azul, fornecedor=laranja, itens=slate,
// totais/financeiro=verde, xml/observação=âmbar), não é um código de status.
const SEC_COLOR = {
  azul:    { fundo: '#eff6ff', texto: '#1a6ea8', barra: '#1a6ea8' },
  roxo:    { fundo: '#f5f3ff', texto: '#6d28d9', barra: '#8b5cf6' },
  slate:   { fundo: '#f3f4f6', texto: '#475569', barra: '#94a3b8' },
  verde:   { fundo: '#f0fdf4', texto: '#15803d', barra: '#22c55e' },
  ambar:   { fundo: '#fffbeb', texto: '#92400e', barra: '#f59e0b' },
  laranja: { fundo: '#fff7ed', texto: '#c2410c', barra: '#f97316' },
} as const

const secTitle = (title: string, cor: keyof typeof SEC_COLOR = 'slate') => {
  const c = SEC_COLOR[cor]
  return (
    <div style={{
      background: c.fundo, borderTop: `1px solid ${c.barra}55`, borderBottom: `1px solid ${c.barra}55`,
      borderLeft: `3px solid ${c.barra}`,
      padding: '4px 1.25rem', fontSize: '0.68rem', fontWeight: 700,
      color: c.texto, textTransform: 'uppercase' as const, letterSpacing: '0.06em',
    }}>{title}</div>
  )
}

// Rótulo/valor de cada campo herda a cor da seção onde está (mesma paleta do
// secTitle) — antes ficava tudo cinza/preto, difícil de escanear rápido.
const kv = (label: string, value: any, cor: keyof typeof SEC_COLOR = 'slate') => {
  const c = SEC_COLOR[cor]
  return (
    <div>
      <div style={{ fontSize: '0.66rem', fontWeight: 700, color: `${c.barra}b0`, textTransform: 'uppercase' as const, marginBottom: 1 }}>{label}</div>
      <div style={{ fontSize: '0.82rem', color: c.texto, fontWeight: 500 }}>{value ?? '—'}</div>
    </div>
  )
}

// Badge colorido pro código do Tipo de Documento (Doc) na grade de resultado —
// cores fixas pros mais comuns (NFE/NSE/CTE), cinza neutro pros demais.
const DOC_COR: Record<string, { fundo: string; texto: string }> = {
  NFE: { fundo: '#dbeafe', texto: '#1e40af' },
  NSE: { fundo: '#ede9fe', texto: '#6d28d9' },
  CTE: { fundo: '#fef3c7', texto: '#92400e' },
}
function badgeDoc(doc: string) {
  const c = DOC_COR[doc] ?? { fundo: '#f1f5f9', texto: '#475569' }
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 10,
      fontSize: '0.72rem', fontWeight: 700, background: c.fundo, color: c.texto,
    }}>{doc || '-'}</span>
  )
}

function Grupo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <label style={{
        fontSize: '0.66rem', fontWeight: 700, color: '#1a6ea8',
        textTransform: 'uppercase', letterSpacing: '0.05em',
      }}>
        {label}
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
        {children}
      </div>
    </div>
  )
}

export default function ConsultaNotas() {
  const { empresaCod } = useEmpresa()
  const { cstDes, classTribDes } = useIbsCbsDescricoes()
  const [searchParams] = useSearchParams()
  const numeroUrl = searchParams.get('numero') || ''

  function filtroPadrao(): Filtros {
    const emp = String(empresaCod ?? 1)
    return {
      empcodDe: emp, empcodAte: emp,
      dataDe: hojeISO(), dataAte: hojeISO(), tipoData: 'recebimento',
      ftvTipdco: '', ftvTipdcoDes: '', modelo: '', modeloDes: '',
      tnoCod: '', tnoDes: '', cprCod: '', cprDes: '', cliCod: '', cliDes: '',
      serie: '', numero: '', valorDe: '', valorAte: '',
    }
  }

  const [lista, setLista] = useState<LinhaNota[]>([])
  const [totalNotas, setTotalNotas] = useState(0)
  const [valorTotal, setValorTotal] = useState(0)
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [imprimindo, setImprimindo] = useState('')

  const [filtros, setFiltros] = useState<Filtros>(filtroPadrao)
  const [pesquisaAtiva, setPesquisaAtiva] = useState<Filtros | null>(null)

  function paramsDeFiltro(f: Filtros): Record<string, string> {
    const params: Record<string, string> = {}
    if (f.empcodDe) params.empcodDe = f.empcodDe
    if (f.empcodAte) params.empcodAte = f.empcodAte
    if (!f.numero) {
      if (f.dataDe) params.dataDe = f.dataDe
      if (f.dataAte) params.dataAte = f.dataAte
    }
    params.tipoData = f.tipoData
    if (f.ftvTipdco) params.ftvTipdco = f.ftvTipdco
    if (f.modelo) params.modelo = f.modelo
    if (f.tnoCod) params.tnoCod = f.tnoCod
    if (f.cprCod) params.cprCod = f.cprCod
    if (f.cliCod) params.cliCod = f.cliCod
    if (f.serie) params.serie = f.serie
    if (f.numero) params.numero = f.numero
    if (f.valorDe) params.valorDe = f.valorDe
    if (f.valorAte) params.valorAte = f.valorAte
    return params
  }

  const carregar = useCallback(async () => {
    if (!pesquisaAtiva) return
    setCarregando(true); setErro(null)
    try {
      const r = await api.get('/erp/notas-entrada/consulta', { params: paramsDeFiltro(pesquisaAtiva) })
      setLista(r.data.notas)
      setTotalNotas(r.data.totalNotas)
      setValorTotal(r.data.valorTotal)
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao consultar notas.')
    } finally {
      setCarregando(false)
    }
  }, [pesquisaAtiva])

  useEffect(() => { carregar() }, [carregar])
  useEffect(() => {
    // Chegando do Manifestador com ?numero= (nota já digitada) — pesquisa
    // direto por esse número em vez do filtro padrão do dia.
    const p = numeroUrl ? { ...filtroPadrao(), numero: numeroUrl } : filtroPadrao()
    setFiltros(p)
    setPesquisaAtiva(p)
  }, [empresaCod]) // eslint-disable-line react-hooks/exhaustive-deps

  function pesquisar() { setPesquisaAtiva({ ...filtros }) }
  function limpar() { const p = filtroPadrao(); setFiltros(p); setPesquisaAtiva(p) }

  const [detalhe, setDetalhe] = useState<{ empcod: number; serie: string; numero: number; cliCod: string } | null>(null)
  const [detalheDados, setDetalheDados] = useState<{ capa: CapaDetalhe; itens: ItemDetalhe[]; parcelas: ParcelaDetalhe[] } | null>(null)
  const [detalheCarregando, setDetalheCarregando] = useState(false)
  const [detalheErro, setDetalheErro] = useState<string | null>(null)
  const [itensAbertos, setItensAbertos] = useState<Set<number>>(new Set())
  const [itensImpostosAbertos, setItensImpostosAbertos] = useState<Set<number>>(new Set())
  const [abaIbsCbsItem, setAbaIbsCbsItem] = useState<Record<number, 'regular' | 'mono'>>({})
  function alternar(set: React.Dispatch<React.SetStateAction<Set<number>>>, lin: number) {
    set(prev => {
      const novo = new Set(prev)
      if (novo.has(lin)) novo.delete(lin); else novo.add(lin)
      return novo
    })
  }

  const abriuAutoRef = useRef(false)
  useEffect(() => {
    if (!numeroUrl || abriuAutoRef.current || carregando) return
    if (lista.length === 1) {
      abriuAutoRef.current = true
      abrirDetalhe(lista[0])
    }
  }, [lista, carregando]) // eslint-disable-line react-hooks/exhaustive-deps

  // DANFE/DACTE/DANFSe pelo XML guardado — mesma impressão do manifestador, sem dar acesso aos eventos
  async function imprimirDocumento(row: LinhaNota) {
    if (!row.chave || !row.documento) return
    setImprimindo(row.chave)
    setErro(null)
    try {
      const r = await api.get(`/erp/notas-entrada/documento/${row.chave}`, { responseType: 'blob' })
      window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank')
    } catch (e: any) {
      let msg = `Erro ao gerar o ${row.documento}.`
      try { if (e?.response?.data instanceof Blob) msg = JSON.parse(await e.response.data.text()).error || msg } catch {}
      setErro(msg)
    } finally {
      setImprimindo('')
    }
  }

  async function abrirDetalhe(row: LinhaNota) {
    setDetalhe({ empcod: row.empcod, serie: row.serie, numero: row.numero, cliCod: row.cliCod })
    setDetalheDados(null); setDetalheErro(null); setDetalheCarregando(true)
    setItensAbertos(new Set()); setItensImpostosAbertos(new Set()); setAbaIbsCbsItem({})
    try {
      // Série pode vir vazia (algumas notas antigas não tem série) — usa um espaço
      // como placeholder no path pra não gerar barra dupla, que o Express não casa
      // com a rota (TRIM() no backend equipara espaço e vazio na comparação).
      const serieUrl = encodeURIComponent(row.serie || ' ')
      const r = await api.get(`/erp/notas-entrada/${row.empcod}/${serieUrl}/${row.numero}/${row.cliCod}/detalhe`)
      setDetalheDados(r.data)
    } catch (e: any) {
      setDetalheErro(e?.response?.data?.error || 'Erro ao carregar a nota.')
    } finally {
      setDetalheCarregando(false)
    }
  }
  function fecharDetalhe() { setDetalhe(null); setDetalheDados(null); setDetalheErro(null) }

  useEffect(() => {
    if (!detalhe) return
    function aoTeclar(e: KeyboardEvent) {
      if (e.key === 'Escape') fecharDetalhe()
    }
    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  }, [detalhe])

  const inp: React.CSSProperties = {
    border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
    fontSize: '0.82rem', height: 30, background: '#fff',
  }
  const th: React.CSSProperties = {
    padding: '9px 10px', textAlign: 'left', color: '#fff', fontWeight: 700,
    fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap',
  }
  const td: React.CSSProperties = { padding: '8px 10px', color: '#374151', whiteSpace: 'nowrap' }

  return (
    <div className="card">
      <h2 style={{
        fontSize: '1.1rem', fontWeight: 700, margin: '0 0 1rem', color: '#1a6ea8',
        paddingBottom: '0.6rem', borderBottom: '2px solid #eff6ff',
      }}>
        Consulta de Notas Fiscais de Entrada
      </h2>

      <div style={{
        background: '#f7faff', border: '1px solid #eef0f4', borderLeft: '4px solid #1a6ea8',
        borderRadius: '0.5rem', padding: '0.7rem 0.9rem', marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem 1.4rem', alignItems: 'flex-end' }}>
            <Grupo label="Empresa">
              <input style={{ ...inp, width: 34, textAlign: 'center' }} type="text" inputMode="numeric" maxLength={2}
                value={filtros.empcodDe}
                onChange={e => setFiltros(f => ({ ...f, empcodDe: e.target.value.replace(/\D/g, '').slice(0, 2) }))} />
              <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>a</span>
              <input style={{ ...inp, width: 34, textAlign: 'center' }} type="text" inputMode="numeric" maxLength={2}
                value={filtros.empcodAte}
                onChange={e => setFiltros(f => ({ ...f, empcodAte: e.target.value.replace(/\D/g, '').slice(0, 2) }))} />
            </Grupo>
            <Grupo label="Data Inicial">
              <input style={{ ...inp, width: 140 }} type="date" value={filtros.dataDe}
                onChange={e => setFiltros(f => ({ ...f, dataDe: e.target.value }))} />
            </Grupo>
            <Grupo label="Data Final">
              <input style={{ ...inp, width: 140 }} type="date" value={filtros.dataAte}
                onChange={e => setFiltros(f => ({ ...f, dataAte: e.target.value }))} />
            </Grupo>
            <Grupo label="Tipo Data">
              <select style={{ ...inp, width: 130 }} value={filtros.tipoData}
                onChange={e => setFiltros(f => ({ ...f, tipoData: e.target.value as Filtros['tipoData'] }))}>
                <option value="recebimento">Recebimento</option>
                <option value="emissao">Emissão</option>
              </select>
            </Grupo>
            <Grupo label="Série">
              <input style={{ ...inp, width: 60 }} value={filtros.serie}
                onChange={e => setFiltros(f => ({ ...f, serie: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && pesquisar()} />
            </Grupo>
            <Grupo label="Número">
              <input style={{ ...inp, width: 90 }} type="number" value={filtros.numero}
                onChange={e => setFiltros(f => ({ ...f, numero: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && pesquisar()} />
            </Grupo>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem 1.4rem', alignItems: 'flex-end', paddingTop: '0.6rem', borderTop: '1px solid #e5eaf3' }}>
            <Grupo label="Documento">
              <div style={{ width: 90 }}>
                <LookupSelect
                  endpoint="/erp/doccad/lista"
                  codKey="doc_cod" descKey="doc_des"
                  codValue={filtros.ftvTipdco} descValue={filtros.ftvTipdco}
                  onSelect={(cod, des) => setFiltros(f => ({ ...f, ftvTipdco: cod, ftvTipdcoDes: des }))}
                  placeholder="Todos..." listWidth={260}
                />
              </div>
            </Grupo>
            <Grupo label="Modelo">
              <div style={{ width: 90 }}>
                <LookupSelect
                  endpoint="/erp/moddoc/lista"
                  codKey="moddoc_cod" descKey="moddoc_des" tagKey="moddoc_cod"
                  codValue={filtros.modelo} descValue={filtros.modeloDes} valorExibido={filtros.modelo}
                  onSelect={(cod, des) => setFiltros(f => ({ ...f, modelo: cod, modeloDes: des }))}
                  placeholder="Todos..." listWidth={380}
                />
              </div>
            </Grupo>
            <Grupo label="Natureza">
              <div style={{ width: 90 }}>
                <LookupSelect
                  endpoint="/erp/naturezas-operacao/lista"
                  codKey="tno_cod" descKey="tno_des"
                  renderItem={(item: any) => (
                    <>
                      <div className="nat-item-linha1"><span className="nat-item-cod">{item.tno_cod}</span></div>
                      <div className="nat-item-dsc">{item.tno_des}</div>
                    </>
                  )}
                  codValue={filtros.tnoCod} descValue={filtros.tnoCod}
                  onSelect={(cod, des) => setFiltros(f => ({ ...f, tnoCod: cod, tnoDes: des }))}
                  placeholder="Todas..." listWidth={320}
                />
              </div>
            </Grupo>
            <Grupo label="Comprador">
              <div style={{ width: 200 }}>
                <LookupSelect
                  endpoint="/compradores"
                  codKey="cpr_cod" descKey="cpr_des"
                  codValue={filtros.cprCod} descValue={filtros.cprDes}
                  onSelect={(cod, des) => setFiltros(f => ({ ...f, cprCod: cod, cprDes: des }))}
                  placeholder="Todos..." listWidth={260}
                />
              </div>
            </Grupo>
            <Grupo label="Fornecedor">
              <div style={{ width: 240 }}>
                <LookupSelect
                  endpoint="/erp/clientes/lista"
                  codKey="cli_cod" descKey="cli_fan"
                  renderItem={(item: any) => (
                    <>
                      <div className="nat-item-linha1"><span className="nat-item-cod">{item.cli_cod}</span></div>
                      <div className="nat-item-dsc">{item.cli_fan || item.cli_nom}</div>
                    </>
                  )}
                  codValue={filtros.cliCod} descValue={filtros.cliDes}
                  onSelect={(cod, des) => setFiltros(f => ({ ...f, cliCod: cod, cliDes: des }))}
                  placeholder="Todos..." listWidth={320} somenteComTexto
                />
              </div>
            </Grupo>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem 1.4rem', alignItems: 'flex-end', paddingTop: '0.6rem', borderTop: '1px solid #e5eaf3' }}>
            <Grupo label="Valor de">
              <input style={{ ...inp, width: 110 }} type="number" value={filtros.valorDe}
                onChange={e => setFiltros(f => ({ ...f, valorDe: e.target.value }))} placeholder="0,00" />
            </Grupo>
            <Grupo label="Valor até">
              <input style={{ ...inp, width: 110 }} type="number" value={filtros.valorAte}
                onChange={e => setFiltros(f => ({ ...f, valorAte: e.target.value }))} placeholder="999.999,99" />
            </Grupo>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.7rem' }}>
          <button className="btn btn-primary btn-sm" onClick={pesquisar} disabled={carregando} style={{ height: 30 }}>
            {carregando ? 'Buscando...' : 'Pesquisar'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={limpar} style={{ height: 30 }}>
            Limpar
          </button>
        </div>
      </div>

      {erro && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erro}</div>}

      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
        <table style={{ fontSize: '0.8rem', minWidth: 1300, borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr style={{ background: '#1a6ea8' }}>
              <th style={{ ...th, textAlign: 'center', width: 40 }}>Emp</th>
              <th style={th}>Série</th>
              <th style={th}>N.Fiscal</th>
              <th style={th}>Doc</th>
              <th style={th}>Mod</th>
              <th style={th}>Natureza</th>
              <th style={th}>Dat.Emi</th>
              <th style={th}>Dat.Ent</th>
              <th style={th}>Fornecedor</th>
              <th style={th}>CNPJ Fornecedor</th>
              <th style={th}>Comprador</th>
              <th style={{ ...th, textAlign: 'right' }}>Val.Merc</th>
              <th style={{ ...th, textAlign: 'right' }}>Val.Nota</th>
              <th style={th}>Usuário</th>
              <th style={{ ...th, textAlign: 'center' }}>DANFE/DACTE/DANFSe</th>
            </tr>
          </thead>
          <tbody>
            {carregando && (
              <tr><td colSpan={15} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Carregando...</td></tr>
            )}
            {!carregando && lista.length === 0 && (
              <tr><td colSpan={15} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Nenhuma nota encontrada</td></tr>
            )}
            {lista.map((row, idx) => (
              <tr key={`${row.empcod}-${row.serie}-${row.numero}-${row.cliCod}`} style={{
                background: idx % 2 === 0 ? '#fff' : '#f7faff',
                borderBottom: '1px solid #e5e7eb',
              }}>
                <td style={{ ...td, textAlign: 'center' }}>{row.empcod}</td>
                <td style={td}>{row.serie}</td>
                <td style={{ ...td, fontWeight: 700 }}>
                  <span onClick={() => abrirDetalhe(row)}
                    style={{ cursor: 'pointer', color: '#1d4ed8', textDecoration: 'underline' }}
                    title="Clique para ver os dados completos da nota">
                    {row.numero}
                  </span>
                </td>
                <td style={td}>{badgeDoc(row.ftvTipdco)}</td>
                <td style={{ ...td, fontFamily: 'monospace', color: '#6b7280' }}>{row.modelo}</td>
                <td style={td}>{row.naturezaDes || '-'}</td>
                <td style={td}>{fmtData(row.dataEmissao)}</td>
                <td style={td}>{fmtData(row.dataRecebimento)}</td>
                <td style={td}>{row.fornecedor || '-'}</td>
                <td style={{ ...td, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{row.cliCod ? fmtCnpj(row.cliCod) : '-'}</td>
                <td style={td}>{row.comprador || '-'}</td>
                <td style={{ ...td, textAlign: 'right', color: '#6b7280' }}>{fmtVal(row.valorMercadoria)}</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#15803d' }}>{fmtVal(row.valorTotal)}</td>
                <td style={td}>{row.usuarioLancamento || '-'}</td>
                <td style={{ ...td, textAlign: 'center' }}>
                  <button className="btn btn-sm" disabled={!row.documento || imprimindo === row.chave}
                    title={row.documento ? `Imprimir ${row.documento}` : 'Sem XML da nota no sistema (não veio pela Distribuição DF-e)'}
                    onClick={() => imprimirDocumento(row)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0.2rem 0.6rem', fontSize: '0.72rem', fontWeight: 700,
                      background: row.documento ? '#1a6ea8' : '#e5e7eb', color: row.documento ? '#fff' : '#9ca3af', border: 'none', whiteSpace: 'nowrap' }}>
                    🖨 {imprimindo === row.chave ? 'Gerando...' : row.documento === 'DANFSE' ? 'DANFSe' : row.documento || 'Sem XML'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem' }}>
        <div style={{
          background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6,
          padding: '0.5rem 1.1rem', minWidth: 160,
        }}>
          <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#1a6ea8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Nro de NFs no Período
          </div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1a6ea8', fontVariantNumeric: 'tabular-nums' }}>
            {totalNotas}
          </div>
        </div>
        <div style={{
          background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6,
          padding: '0.5rem 1.1rem', minWidth: 200,
        }}>
          <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Valor Total
          </div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#15803d', fontVariantNumeric: 'tabular-nums' }}>
            R$ {fmtVal(valorTotal)}
          </div>
        </div>
      </div>

      {detalhe && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          zIndex: 1000, padding: '16px 10px',
        }} onClick={fecharDetalhe}>
          <div style={{
            background: '#fff', borderRadius: 8,
            width: 920, maxWidth: '97vw', maxHeight: 'calc(100vh - 32px)', overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
          }} onClick={e => e.stopPropagation()}>

            {/* Cabeçalho azul */}
            <div style={{
              background: '#1a6ea8', color: '#fff', flexShrink: 0,
              padding: '0.85rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.02rem', letterSpacing: 0.2 }}>
                  Nota Fiscal Nº {detalhe.numero} — Série {detalhe.serie}
                </div>
                <div style={{ fontSize: '0.78rem', opacity: 0.8, marginTop: 3 }}>
                  Estabelecimento {detalhe.empcod}
                </div>
              </div>
              <button onClick={fecharDetalhe} style={{
                background: 'rgba(255,255,255,0.18)', border: 'none', cursor: 'pointer',
                color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: '1rem', lineHeight: 1,
              }}>✕</button>
            </div>

            {/* Corpo scrollável */}
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {detalheCarregando && (
                <div style={{ textAlign: 'center', padding: '4rem', color: '#9ca3af' }}>Carregando dados da nota...</div>
              )}
              {!detalheCarregando && detalheErro && (
                <div style={{ textAlign: 'center', padding: '4rem', color: '#b91c1c' }}>{detalheErro}</div>
              )}

              {!detalheCarregando && detalheDados && (() => {
                const capa = detalheDados.capa
                const itensComTributo = detalheDados.itens.map(it => {
                  const isValor = (Number(it.pnv_is_bas) || 0) * (Number(it.pnv_is_are) || 0) / 100
                  const tributos = [
                    { n: 'ICMS',    cst: it.pnv_tricod, base: it.pnv_bt_icm, aliq: it.pnv_pe_icm, valor: it.pnv_vl_icm },
                    { n: 'ICMS ST', cst: null,          base: it.pnv_bt_sub, aliq: it.pnv_pe_sub, valor: it.pnv_vl_sub },
                    { n: 'IPI',     cst: it.pnv_triipi, base: it.pnv_bt_ipi, aliq: it.pnv_pe_ipi, valor: it.pnv_vl_ipi },
                    { n: 'PIS',     cst: it.pnv_tripis, base: it.pnv_bt_pis, aliq: it.pnv_pe_pis, valor: it.pnv_vl_pis },
                    { n: 'COFINS',  cst: it.pnv_tricof, base: it.pnv_bt_cof, aliq: it.pnv_pe_cof, valor: it.pnv_vl_cof },
                    { n: 'INSS',    cst: null,          base: it.pnv_bt_ins, aliq: it.pnv_pe_ins, valor: it.pnv_vl_ins },
                    { n: 'IS (Seletivo)', cst: null,    base: it.pnv_is_bas, aliq: it.pnv_is_are, valor: isValor },
                    // IS (Seletivo) ainda não se aplica na prática — só mostra se tiver valor de verdade,
                    // diferente dos outros tributos aqui em cima que sempre aparecem (mesmo zerados).
                  ].filter(t => t.n !== 'IS (Seletivo)' || Number(t.valor) !== 0 || Number(t.base) !== 0 || Number(t.aliq) !== 0)
                  const ibsLinhas = [
                    { n: 'CBS', bas: it.pnv_cb_bas, bce: it.pnv_cb_bce, aliRef: it.pnv_cb_are, red: it.pnv_cb_ral, aliEf: it.pnv_cb_aef, valor: it.pnv_cb_val },
                    { n: `IBS UF (${(it.pnv_ib_est || '').trim() || '?'})`, bas: null, bce: it.pnv_ib_bce, aliRef: it.pnv_ib_are, red: it.pnv_ib_ral, aliEf: it.pnv_ib_aef, valor: it.pnv_ib_val },
                    { n: 'IBS Municipal', bas: it.pnv_mu_bas, bce: it.pnv_mu_bce, aliRef: it.pnv_mu_are, red: it.pnv_mu_ral, aliEf: it.pnv_mu_aef, valor: it.pnv_mu_val },
                  ]
                  const xmlRef = [
                    { n: 'PIS (XML)',    cst: it.pnv_pisxml_cst, base: it.pnv_pisxml_bas, aliq: it.pnv_pisxml_ali, valor: it.pnv_pisxml_val },
                    { n: 'COFINS (XML)', cst: it.pnv_cofxml_cst, base: it.pnv_cofxml_bas, aliq: it.pnv_cofxml_ali, valor: it.pnv_cofxml_val },
                  ]
                  return { ...it, tributos, ibsLinhas, xmlRef }
                })
                const somaTributo = (fn: (it: ItemDetalhe) => number) => detalheDados.itens.reduce((a, it) => a + (fn(it) || 0), 0)
                const chips = [
                  { l: 'ICMS',   v: somaTributo(it => Number(it.pnv_vl_icm)) },
                  { l: 'ICMS ST', v: somaTributo(it => Number(it.pnv_vl_sub)) },
                  { l: 'IPI',    v: somaTributo(it => Number(it.pnv_vl_ipi)) },
                  { l: 'PIS',    v: somaTributo(it => Number(it.pnv_vl_pis)) },
                  { l: 'COFINS', v: somaTributo(it => Number(it.pnv_vl_cof)) },
                  { l: 'INSS',   v: somaTributo(it => Number(it.pnv_vl_ins)) },
                  { l: 'IS',     v: somaTributo(it => (Number(it.pnv_is_bas) || 0) * (Number(it.pnv_is_are) || 0) / 100) },
                  { l: 'CBS',    v: somaTributo(it => Number(it.pnv_cb_val)) },
                  { l: 'IBS UF', v: somaTributo(it => Number(it.pnv_ib_val)) },
                  { l: 'IBS Mun', v: somaTributo(it => Number(it.pnv_mu_val)) },
                ].filter(c => c.v > 0)

                return (
                  <>
                    {/* ── IDENTIFICAÇÃO ── */}
                    {secTitle('Identificação', 'azul')}
                    <div style={{ padding: '0.7rem 1.25rem' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.45rem 1.5rem', marginBottom: '0.45rem' }}>
                        {kv('Empresa', capa.empNome ? `${capa.empcod} - ${capa.empNome}` : capa.empcod, 'azul')}
                        {kv('Série / Número', `${capa.serie} / ${capa.numero}`, 'azul')}
                        {kv('Documento', `${capa.ftvTipdco} — Modelo ${capa.modelo}`, 'azul')}
                        {kv('CFOP', capa.tnoCdf ?? '—', 'azul')}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.45rem 1.5rem', marginBottom: '0.45rem' }}>
                        {kv('Natureza', capa.tnoCod ? `${capa.tnoCod} - ${capa.naturezaDes}` : '—', 'azul')}
                        {kv('Data de Emissão', fmtData(capa.dataEmissao), 'azul')}
                        {kv('Data de Recebimento', fmtData(capa.dataRecebimento), 'azul')}
                        {kv('Comprador', capa.comprador || '—', 'azul')}
                      </div>
                      {capa.chave && kv('Chave de Acesso',
                        <span style={{ fontFamily: 'monospace', fontSize: '0.77rem', wordBreak: 'break-all' }}>{capa.chave}</span>,
                        'azul'
                      )}
                    </div>

                    {/* ── FORNECEDOR ── */}
                    {secTitle('Fornecedor', 'laranja')}
                    <div style={{ padding: '0.7rem 1.25rem' }}>
                      <div style={{ marginBottom: '0.4rem' }}>
                        {kv('Razão Social', <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{capa.fornecedorNome || '—'}</span>, 'laranja')}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.4rem 1.5rem' }}>
                        {kv('CNPJ / CPF', fmtCnpj(capa.cliCod), 'laranja')}
                      </div>
                    </div>

                    {/* ── ITENS ── */}
                    {secTitle(`Itens${itensComTributo.length ? ` (${itensComTributo.length})` : ''}`)}
                    <div style={{ padding: '0.7rem 1.25rem' }}>
                      {itensComTributo.length === 0 ? (
                        <span style={{ fontSize: '0.82rem', color: '#9ca3af' }}>Nenhum item lançado para esta nota.</span>
                      ) : itensComTributo.map(it => {
                        const qty = Number(it.pnv_qt_ven) || 0
                        const total = Number(it.pnv_vl_mer) || 0
                        const aberto = itensAbertos.has(it.pnv_lin)
                        const abertoImp = itensImpostosAbertos.has(it.pnv_lin)
                        const botao = (ativo: boolean, cor: 'azul' | 'roxo'): React.CSSProperties => {
                          const c = cor === 'azul' ? { linha: '#1a6ea8', fundo: '#eff6ff', texto: '#1a6ea8' } : { linha: '#8b5cf6', fundo: '#f5f3ff', texto: '#6d28d9' }
                          return {
                            border: '1px solid ' + (ativo ? c.linha : '#d1d5db'), borderRadius: 4,
                            background: ativo ? c.fundo : '#fff', color: ativo ? c.texto : '#6b7280',
                            fontSize: '0.72rem', fontWeight: 700, padding: '3px 10px', cursor: 'pointer',
                          }
                        }
                        return (
                          <div key={it.pnv_lin} style={{ border: '1px solid #e5e7eb', borderRadius: 6, marginBottom: '0.6rem', overflow: 'hidden' }}>
                            <div style={{
                              display: 'grid', gridTemplateColumns: '1.8fr 0.7fr 0.8fr 1fr auto auto',
                              gap: '0.5rem', padding: '0.5rem 0.75rem',
                              background: '#f9fafb', borderBottom: (aberto || abertoImp) ? '1px solid #e5e7eb' : 'none', alignItems: 'center',
                            }}>
                              <div>
                                <span style={{ fontSize: '0.68rem', color: '#9ca3af', marginRight: 5 }}>#{it.its_cod}</span>
                                <span style={{ fontWeight: 700 }}>{it.pnv_des || '—'}</span>
                                {it.pnv_de1 && <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 1 }}>{it.pnv_de1}</div>}
                              </div>
                              <div style={{ fontSize: '0.82rem' }}>
                                <span style={{ fontWeight: 600 }}>{qty.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                                {it.pnv_un && <span style={{ color: '#9ca3af', marginLeft: 4, fontSize: '0.76rem' }}>{it.pnv_un}</span>}
                              </div>
                              {/* Valor unitário (pnv_precus = vUnCom); nota antiga sem ele cai no total ÷ quantidade */}
                              <div style={{ fontSize: '0.82rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} title="Valor unitário">
                                <span style={{ color: '#9ca3af', fontSize: '0.7rem', marginRight: 4 }}>Unit.</span>
                                <span style={{ fontWeight: 600 }}>
                                  R$ {(Number(it.pnv_precus) || (qty ? total / qty : 0)).toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                                </span>
                              </div>
                              <div style={{ fontSize: '0.92rem', fontWeight: 700, textAlign: 'right', color: '#15803d', fontVariantNumeric: 'tabular-nums' }}>
                                R$ {fmtVal(total)}
                              </div>
                              <button style={botao(aberto, 'azul')} onClick={() => alternar(setItensAbertos, it.pnv_lin)}>
                                {aberto ? '▴ Item' : '▾ Item'}
                              </button>
                              <button style={botao(abertoImp, 'roxo')} onClick={() => alternar(setItensImpostosAbertos, it.pnv_lin)}>
                                {abertoImp ? '▴ Impostos' : '▾ Impostos'}
                              </button>
                            </div>

                            {aberto && (
                            <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.79rem', background: '#f8faff', borderBottom: abertoImp ? '1px solid #e5e7eb' : 'none' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.4rem 1.5rem', marginBottom: '0.5rem' }}>
                                {kv('CFOP', it.cfop ? `${it.cfop}${it.naturezaItemDes ? ` - ${it.naturezaItemDes}` : ''}` : '—', 'azul')}
                                {kv('Origem', it.pnv_orig ? `${it.pnv_orig}${it.origemDes ? ` - ${it.origemDes}` : ''}` : '—', 'azul')}
                                {kv('Destino', it.pnv_dest ? `${it.pnv_dest}${it.destinoDes ? ` - ${it.destinoDes}` : ''}` : '—', 'azul')}
                                {it.pnv_ncm && kv('NCM', it.pnv_ncm, 'azul')}
                                {it.pnv_servi && kv('NBS', `${it.pnv_servi}${it.nbsDes ? ` - ${it.nbsDes}` : ''}`, 'azul')}
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.4rem 1.5rem' }}>
                                {it.rateio.length <= 1 && it.pnv_ccucod && kv('Centro de Custo', `${it.pnv_ccucod}${it.ccuDes ? ` - ${it.ccuDes}` : ''}`, 'azul')}
                                {it.rateio.length <= 1 && it.pnv_aplcod && kv('Aplicação', `${it.pnv_aplcod}${it.aplDes ? ` - ${it.aplDes}` : ''}`, 'azul')}
                                {Number(it.pnv_vl_fre) > 0 && kv('Frete do Item', `R$ ${fmtVal(it.pnv_vl_fre)}`, 'azul')}
                                {Number(it.pnv_vl_seg) > 0 && kv('Seguro do Item', `R$ ${fmtVal(it.pnv_vl_seg)}`, 'azul')}
                                {Number(it.pnv_vl_dsc) > 0 && kv('Desconto do Item', `R$ ${fmtVal(it.pnv_vl_dsc)}`, 'azul')}
                                {Number(it.pnv_vl_acr) > 0 && kv('Acréscimo do Item', `R$ ${fmtVal(it.pnv_vl_acr)}`, 'azul')}
                              </div>
                              {it.rateio.length > 1 && (
                                <div style={{ marginTop: '0.5rem' }}>
                                  <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#1a6ea8', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.25rem' }}>
                                    Rateio por Centro de Custo
                                  </div>
                                  <table style={{ width: '100%', maxWidth: 620, borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                                    <thead>
                                      <tr style={{ background: '#1a6ea8' }}>
                                        <th style={{ color: '#fff', padding: '3px 6px', textAlign: 'left' }}>Centro de Custo</th>
                                        <th style={{ color: '#fff', padding: '3px 6px', textAlign: 'left' }}>Aplicação</th>
                                        <th style={{ color: '#fff', padding: '3px 6px', textAlign: 'right' }}>%</th>
                                        <th style={{ color: '#fff', padding: '3px 6px', textAlign: 'right' }}>Valor</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {it.rateio.map(r => (
                                        <tr key={r.seq}>
                                          <td style={{ padding: '2px 6px', border: '1px solid #eef0f4' }}>{r.centroCustoCod}{r.centroCustoDes ? ` - ${r.centroCustoDes}` : ''}</td>
                                          <td style={{ padding: '2px 6px', border: '1px solid #eef0f4' }}>{r.aplicacaoCod}{r.aplicacaoDes ? ` - ${r.aplicacaoDes}` : ''}</td>
                                          <td style={{ padding: '2px 6px', border: '1px solid #eef0f4', textAlign: 'right' }}>{r.percentual.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}%</td>
                                          <td style={{ padding: '2px 6px', border: '1px solid #eef0f4', textAlign: 'right' }}>R$ {r.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              {it.pnv_vei && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.4rem 1.5rem', marginTop: '0.5rem' }}>
                                  {kv('Veículo', `${it.pnv_vei}${it.veiculoDes ? ` - ${it.veiculoDes}` : ''}`, 'azul')}
                                  {it.pnv_odo != null && kv('Hodômetro', String(it.pnv_odo), 'azul')}
                                  {it.pnv_oser != null && kv(
                                    'Ordem de Serviço',
                                    `${it.pnv_oser}${it.pnv_oslin != null ? ` (linha ${it.pnv_oslin})` : ''}` +
                                      (it.ordemServicoExecutada === true ? ' — Executada' : it.ordemServicoExecutada === false ? ' — Pendente' : ''),
                                    it.ordemServicoExecutada === true ? 'verde' : 'azul',
                                  )}
                                  {it.pnv_tipser != null && kv('Tipo de Serviço', `${it.pnv_tipser}${it.tipoServicoDes ? ` - ${it.tipoServicoDes}` : ''}`, 'azul')}
                                </div>
                              )}
                              {it.pnv_pnucod != null && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.4rem 1.5rem', marginTop: '0.5rem' }}>
                                  {kv('Pneu', `${it.pnv_pnucod}${it.pneuDes ? ` - ${it.pneuDes}` : ''}`, 'laranja')}
                                  {it.recapagem ? (
                                    <>
                                      {kv('Procedimento', it.recapagem.procedimento, it.recapagem.procedimento === 'Recapagem' ? 'verde' : 'laranja')}
                                      {kv('Valor', `R$ ${it.recapagem.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, 'laranja')}
                                      {it.recapagem.fornecedoraDes && kv('Fornecedora', it.recapagem.fornecedoraDes, 'laranja')}
                                      {it.recapagem.desenhoDes && kv('Novo Desenho', it.recapagem.desenhoDes, 'laranja')}
                                      {it.recapagem.novaVida && kv('Nova Vida', it.recapagem.novaVida, 'laranja')}
                                    </>
                                  ) : (
                                    kv('Recapagem/Conserto', it.pnv_pnurec === 'S' ? 'Recapagem solicitada (ainda não confirmada)' : 'Conserto solicitado (ainda não confirmado)', 'laranja')
                                  )}
                                </div>
                              )}
                            </div>
                            )}

                            {abertoImp && (
                            <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.79rem', background: '#fdfcff' }}>
                              {it.tributos.length === 0 && it.ibsLinhas.length === 0 && it.xmlRef.length === 0 ? (
                                <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>Nenhum imposto lançado neste item.</span>
                              ) : (
                                <>
                                  {it.tributos.length > 0 && (
                                    <div style={{ marginBottom: '0.45rem' }}>
                                      <div style={{ fontSize: '0.64rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3 }}>Impostos Tradicionais</div>
                                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                                        <thead>
                                          <tr style={{ background: '#f0fdf4' }}>
                                            {['Tributo', 'CST', 'Base de Cálculo', 'Alíquota', 'Valor'].map(h => (
                                              <th key={h} style={{ padding: '2px 5px', border: '1px solid #d1fae5', color: '#9ca3af', fontWeight: 600, textAlign: h === 'Tributo' || h === 'CST' ? 'left' : 'right' }}>{h}</th>
                                            ))}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {it.tributos.map((t, ti) => (
                                            <tr key={ti}>
                                              <td style={{ padding: '2px 5px', border: '1px solid #d1fae5', fontWeight: 700 }}>{t.n}</td>
                                              <td style={{ padding: '2px 5px', border: '1px solid #d1fae5', fontFamily: 'monospace' }}>{t.cst ?? '—'}</td>
                                              <td style={{ padding: '2px 5px', border: '1px solid #d1fae5', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{Number(t.base) ? fmtVal(t.base) : '—'}</td>
                                              <td style={{ padding: '2px 5px', border: '1px solid #d1fae5', textAlign: 'right' }}>{Number(t.aliq) ? `${Number(t.aliq).toFixed(2)}%` : '—'}</td>
                                              <td style={{ padding: '2px 5px', border: '1px solid #d1fae5', textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: '#15803d' }}>{Number(t.valor) ? fmtVal(t.valor) : '—'}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                  {(it.pnv_cstrtc || it.pnv_clartc) && (
                                    <div style={{ marginBottom: '0.45rem' }}>
                                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.4rem 1.5rem' }}>
                                        {kv('CST IBS/CBS', it.pnv_cstrtc || '—', 'verde')}
                                        {kv('Classificação Tributária', it.pnv_clartc || '—', 'verde')}
                                      </div>
                                      {(cstDes(it.pnv_cstrtc) || classTribDes(it.pnv_cstrtc, it.pnv_clartc)) && (
                                        <div style={{ fontSize: '0.78rem', color: '#374151', lineHeight: 1.4, marginTop: '0.3rem' }}>
                                          {cstDes(it.pnv_cstrtc) && <div><strong>CST {it.pnv_cstrtc}:</strong> {cstDes(it.pnv_cstrtc)}</div>}
                                          {classTribDes(it.pnv_cstrtc, it.pnv_clartc) && (
                                            <div><strong>cClassTrib {it.pnv_clartc}:</strong> {classTribDes(it.pnv_cstrtc, it.pnv_clartc)}</div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {(it.ibsCbsMono?.length ?? 0) > 0 && (
                                    <AbasIbsCbs cor="#15803d" aba={abaIbsCbsItem[it.pnv_lin] ?? 'mono'}
                                      onChange={a => setAbaIbsCbsItem(m => ({ ...m, [it.pnv_lin]: a }))} />
                                  )}
                                  {(it.ibsCbsMono?.length ?? 0) > 0 && (abaIbsCbsItem[it.pnv_lin] ?? 'mono') === 'mono' && (
                                    <div style={{ marginBottom: '0.45rem' }}>
                                      <TabelaIbsCbsMono linhas={it.ibsCbsMono!} unidade={it.pnv_un} cor="#15803d" />
                                      {it.ibsCbsMonoDoXml && (
                                        <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: 3 }}>Lido do XML da nota (lançada antes desse campo existir).</div>
                                      )}
                                    </div>
                                  )}
                                  {it.ibsLinhas.length > 0 && ((it.ibsCbsMono?.length ?? 0) === 0 || abaIbsCbsItem[it.pnv_lin] === 'regular') && (
                                    <div style={{ marginBottom: '0.45rem' }}>
                                      <div style={{ fontSize: '0.64rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3 }}>IBS / CBS</div>
                                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                                        <thead>
                                          <tr style={{ background: '#f0fdf4' }}>
                                            {['Componente', 'Base', 'Base Ef.', 'Alíq. Ref.', 'Red. (%)', 'Alíq. Ef.', 'Valor'].map(h => (
                                              <th key={h} style={{ padding: '2px 5px', border: '1px solid #d1fae5', color: '#9ca3af', fontWeight: 600, textAlign: h === 'Componente' ? 'left' : 'right' }}>{h}</th>
                                            ))}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {it.ibsLinhas.map((l, li) => (
                                            <tr key={li}>
                                              <td style={{ padding: '2px 5px', border: '1px solid #d1fae5', fontWeight: 700 }}>{l.n}</td>
                                              <td style={{ padding: '2px 5px', border: '1px solid #d1fae5', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{l.bas != null && Number(l.bas) ? fmtVal(l.bas) : '—'}</td>
                                              <td style={{ padding: '2px 5px', border: '1px solid #d1fae5', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{Number(l.bce) ? fmtVal(l.bce) : '—'}</td>
                                              <td style={{ padding: '2px 5px', border: '1px solid #d1fae5', textAlign: 'right' }}>{Number(l.aliRef) ? `${Number(l.aliRef).toFixed(4)}%` : '—'}</td>
                                              <td style={{ padding: '2px 5px', border: '1px solid #d1fae5', textAlign: 'right' }}>{Number(l.red) ? `${Number(l.red).toFixed(2)}%` : '—'}</td>
                                              <td style={{ padding: '2px 5px', border: '1px solid #d1fae5', textAlign: 'right' }}>{Number(l.aliEf) ? `${Number(l.aliEf).toFixed(4)}%` : '—'}</td>
                                              <td style={{ padding: '2px 5px', border: '1px solid #d1fae5', textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: '#15803d' }}>{Number(l.valor) ? fmtVal(l.valor) : '—'}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                  {it.xmlRef.length > 0 && (
                                    <div>
                                      <div style={{ fontSize: '0.64rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3 }}>
                                        PIS/COFINS declarados no XML do fornecedor (referência — não entra no cálculo fiscal)
                                      </div>
                                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                                        <thead>
                                          <tr style={{ background: '#fffbeb' }}>
                                            {['Tributo', 'CST', 'Base de Cálculo', 'Alíquota', 'Valor'].map(h => (
                                              <th key={h} style={{ padding: '2px 5px', border: '1px solid #fde68a', color: '#92400e', fontWeight: 600, textAlign: h === 'Tributo' || h === 'CST' ? 'left' : 'right' }}>{h}</th>
                                            ))}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {it.xmlRef.map((t, ti) => (
                                            <tr key={ti}>
                                              <td style={{ padding: '2px 5px', border: '1px solid #fde68a', fontWeight: 700 }}>{t.n}</td>
                                              <td style={{ padding: '2px 5px', border: '1px solid #fde68a', fontFamily: 'monospace' }}>{t.cst || '—'}</td>
                                              <td style={{ padding: '2px 5px', border: '1px solid #fde68a', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{Number(t.base) ? fmtVal(t.base) : '—'}</td>
                                              <td style={{ padding: '2px 5px', border: '1px solid #fde68a', textAlign: 'right' }}>{Number(t.aliq) ? `${Number(t.aliq).toFixed(2)}%` : '—'}</td>
                                              <td style={{ padding: '2px 5px', border: '1px solid #fde68a', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{Number(t.valor) ? fmtVal(t.valor) : '—'}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                            )}
                          </div>
                        )
                      })}
                    </div>

                    {/* ── TOTAIS ── */}
                    {secTitle('Totais', 'verde')}
                    <div style={{ padding: '0.7rem 1.25rem', display: 'flex', alignItems: 'flex-start', gap: '2rem', flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 2 }}>Valor Total da Nota</div>
                        <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#15803d', fontVariantNumeric: 'tabular-nums' }}>
                          R$ {fmtVal(capa.valorTotal)}
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, auto)', gap: '0.4rem 1.5rem' }}>
                        {kv('Mercadoria', `R$ ${fmtVal(capa.valorMercadoria)}`, 'verde')}
                        {kv('Frete', `R$ ${fmtVal(capa.valorFrete)}`, 'verde')}
                        {kv('Seguro', `R$ ${fmtVal(capa.valorSeguro)}`, 'verde')}
                        {kv('Desconto', `R$ ${fmtVal(capa.valorDesconto)}`, 'verde')}
                        {kv('Acréscimo', `R$ ${fmtVal(capa.valorAcrescimo)}`, 'verde')}
                      </div>
                      {chips.length > 0 && (
                        <div style={{ flex: 1, minWidth: 260 }}>
                          <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 5 }}>Tributos Totais</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                            {chips.map(c => (
                              <div key={c.l} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 4, padding: '3px 10px', fontSize: '0.77rem' }}>
                                <span style={{ color: '#9ca3af', fontWeight: 700, marginRight: 4 }}>{c.l}</span>
                                <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>R$ {fmtVal(c.v)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {(() => {
                        // Retenções gravadas na capa (ntvcad), iguais aos campos da consulta do FrotaWin
                        const retidos = [
                          { l: 'IRRF', v: capa.retIrrf }, { l: 'INSS', v: capa.retInss }, { l: 'ISSQN', v: capa.retIss },
                          { l: 'PIS', v: capa.retPis }, { l: 'COFINS', v: capa.retCofins }, { l: 'CSLL', v: capa.retCsll },
                        ].filter(r => r.v > 0)
                        if (!retidos.length) return null
                        const totalRetido = retidos.reduce((s, r) => s + r.v, 0)
                        return (
                          <div style={{ flexBasis: '100%' }}>
                            <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 5 }}>Impostos Retidos</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center' }}>
                              {retidos.map(r => (
                                <div key={r.l} style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 4, padding: '3px 10px', fontSize: '0.77rem' }}>
                                  <span style={{ color: '#c2410c', fontWeight: 700, marginRight: 4 }}>{r.l}</span>
                                  <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>R$ {fmtVal(r.v)}</span>
                                </div>
                              ))}
                              <span style={{ fontSize: '0.77rem', color: '#6b7280', marginLeft: 6 }}>
                                Total retido <b style={{ fontVariantNumeric: 'tabular-nums' }}>R$ {fmtVal(totalRetido)}</b>
                                {' · '}Líquido <b style={{ fontVariantNumeric: 'tabular-nums' }}>R$ {fmtVal(capa.valorTotal - totalRetido)}</b>
                              </span>
                            </div>
                          </div>
                        )
                      })()}
                    </div>

                    {/* ── FINANCEIRO ── */}
                    {secTitle('Financeiro', 'verde')}
                    <div style={{ padding: '0.7rem 1.25rem' }}>
                      {detalheDados.parcelas.length === 0 ? (
                        <span style={{ fontSize: '0.82rem', color: '#9ca3af' }}>Nenhum financeiro lançado para esta nota.</span>
                      ) : (
                        <>
                          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#065f46', textTransform: 'uppercase', marginBottom: 6 }}>
                            {detalheDados.parcelas.length} parcela{detalheDados.parcelas.length > 1 ? 's' : ''}
                          </div>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                            <thead>
                              <tr style={{ background: '#f0fdf4' }}>
                                {['Nº', 'Vencimento', 'Conta', 'Valor', 'Saldo'].map(h => (
                                  <th key={h} style={{ padding: '4px 8px', border: '1px solid #bbf7d0', color: '#065f46', fontWeight: 700, textAlign: h === 'Nº' ? 'center' : (h === 'Valor' || h === 'Saldo') ? 'right' : 'left' }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {detalheDados.parcelas.map((p, i) => (
                                <tr key={i}>
                                  <td style={{ padding: '4px 8px', border: '1px solid #bbf7d0', textAlign: 'center' }}>{p.numero}</td>
                                  <td style={{ padding: '4px 8px', border: '1px solid #bbf7d0' }}>{fmtData(p.vencimento)}</td>
                                  <td style={{ padding: '4px 8px', border: '1px solid #bbf7d0' }}>{p.contaCod ? `${p.contaCod}${p.contaDes ? ` - ${p.contaDes}` : ''}` : '—'}</td>
                                  <td style={{ padding: '4px 8px', border: '1px solid #bbf7d0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtVal(p.valor)}</td>
                                  <td style={{ padding: '4px 8px', border: '1px solid #bbf7d0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtVal(p.saldo)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      )}
                    </div>

                    {/* ── OBSERVAÇÃO ── */}
                    {capa.observacao && (
                      <>
                        {secTitle('Observação', 'ambar')}
                        <div style={{ padding: '0.7rem 1.25rem' }}>
                          <div style={{ fontSize: '0.85rem', background: '#f9fafb', borderRadius: 4, padding: '0.5rem 0.75rem', color: '#374151' }}>
                            {capa.observacao}
                          </div>
                        </div>
                      </>
                    )}

                    <div style={{ height: '0.5rem' }} />
                  </>
                )
              })()}
            </div>

            {/* Rodapé */}
            <div style={{
              borderTop: '1px solid #e5e7eb', padding: '0.6rem 1.25rem',
              display: 'flex', justifyContent: 'flex-end', background: '#f9fafb', flexShrink: 0,
            }}>
              <button className="btn btn-sm" onClick={fecharDetalhe} style={{ color: '#6b7280' }}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
